import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { CategoryIcon } from "../components/CategoryIcon";
import ListingCard from "../components/ListingCard";
import AdSlot from "../components/AdSlot";
import { Reveal } from "../components/Reveal";

export default function Directory() {
  const { slug } = useParams();
  const { t, tOr } = useLang();
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [listings, setListings] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [country, setCountry] = useState(params.get("country") || "");
  const [state, setState] = useState(params.get("state") || "");
  const [city, setCity] = useState(params.get("city") || "");

  const activeCategory = slug || params.get("category") || "";
  const catMeta = useMemo(() => categories.find((c) => c.slug === activeCategory), [categories, activeCategory]);
  const activeCountries = countries.filter((c) => c.status === "active");
  const stateOptions = activeCountries.find((c) => c.code === country)?.states || [];
  const cities = stateOptions.find((s) => s.name === state)?.cities || [];

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/countries").then((r) => setCountries(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setListings(null);
    const p = {};
    if (activeCategory) p.category = activeCategory;
    const query = params.get("q");
    if (query) p.q = query;
    if (params.get("country")) p.country = params.get("country");
    if (params.get("state")) p.state = params.get("state");
    if (params.get("city")) p.city = params.get("city");
    api.get("/listings", { params: p }).then((r) => setListings(r.data || [])).catch(() => setListings([]));
  }, [activeCategory, params]);

  const applyFilters = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (activeCategory && !slug) p.set("category", activeCategory);
    if (country) p.set("country", country);
    if (state) p.set("state", state);
    if (city) p.set("city", city);
    setParams(p);
  };

  const title = catMeta ? tOr(`cat.${activeCategory}`, catMeta.label) : params.get("q") ? `${t("dir.resultsfor")} “${params.get("q")}”` : t("dir.alllistings");

  return (
    <div data-testid="directory-page">
      <section className="bg-teak acheik-bg-dark text-cream py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-gold mb-4">
            {catMeta && <CategoryIcon name={catMeta.icon} className="w-4 h-4" />}
            {catMeta ? t("dir.category") : t("dir.directory")}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="directory-title">{title}</h1>
          <p className="mt-3 text-cream/60 max-w-xl text-sm sm:text-base">
            {t("dir.reviewed")}
          </p>
        </div>
      </section>

      <section className="sticky top-16 lg:top-20 z-30 bg-cream/95 backdrop-blur-md border-b border-[#E6DED3] py-4">
        <form onSubmit={applyFilters} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-3" data-testid="directory-filter-form">
          <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-[#E6DED3]">
            <Search className="w-4 h-4 text-stone2 shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("dir.search.ph")}
              className="bg-transparent w-full text-sm outline-none"
              data-testid="directory-search-input"
            />
          </div>
          <div className="flex gap-3">
            <select value={country} onChange={(e) => { setCountry(e.target.value); setState(""); setCity(""); }} className="px-3 py-2.5 rounded-xl bg-white border border-[#E6DED3] text-sm text-clay outline-none" data-testid="directory-country-select">
              <option value="">{t("dir.allcountries")}</option>
              {activeCountries.map((c) => <option key={c.code} value={c.code}>{tOr(`country.${c.code}`, c.name)}</option>)}
            </select>
            <select value={state} onChange={(e) => { setState(e.target.value); setCity(""); }} className="px-3 py-2.5 rounded-xl bg-white border border-[#E6DED3] text-sm text-clay outline-none" data-testid="directory-state-select">
              <option value="">{t("dir.allstates")}</option>
              {stateOptions.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
            <select value={city} onChange={(e) => setCity(e.target.value)} className="px-3 py-2.5 rounded-xl bg-white border border-[#E6DED3] text-sm text-clay outline-none" data-testid="directory-city-select">
              <option value="">{t("dir.allcities")}</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button type="submit" data-testid="directory-filter-apply" className="inline-flex items-center gap-2 bg-ink hover:bg-ruby text-cream text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
              <SlidersHorizontal className="w-4 h-4" /> {t("dir.filter")}
            </button>
          </div>
        </form>
      </section>

      <section className="py-10 lg:py-14 bg-cream min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeCategory && <AdSlot placement="category_top" category={activeCategory} className="mb-10" />}
          {listings === null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-80 rounded-2xl bg-sand animate-pulse" />
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-20" data-testid="directory-empty">
              <p className="font-serif italic text-3xl text-gold-dark mb-3">{t("dir.empty.t")}</p>
              <p className="text-clay text-sm">{t("dir.empty.s")}</p>
            </div>
          ) : (
            <>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-stone2 mb-6" data-testid="directory-count">
                {listings.length} {listings.length !== 1 ? t("dir.count.many") : t("dir.count.one")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="directory-grid">
                {listings.map((l, i) => (
                  <Reveal key={l.id} delay={Math.min(i, 6) * 0.05} className="h-full">
                    <ListingCard listing={l} />
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
